import { Component, type ReactNode, type ErrorInfo, type CSSProperties } from 'react'
import { Button } from '@/components/base/Button'

// ============================================
// Types
// ============================================

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

// ============================================
// Styles
// ============================================

const containerStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: '24px',
  backgroundColor: 'var(--color-paper)',
  textAlign: 'center',
}

const iconContainerStyles: CSSProperties = {
  width: '64px',
  height: '64px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-error-light, rgba(220, 38, 38, 0.1))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
}

const titleStyles: CSSProperties = {
  fontFamily: 'var(--font-brand)',
  fontSize: '1.5rem',
  fontWeight: 600,
  color: 'var(--color-ink)',
  margin: '0 0 8px',
}

const messageStyles: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '1rem',
  color: 'var(--color-ink-tertiary)',
  margin: '0 0 24px',
  maxWidth: '400px',
  lineHeight: 1.5,
}

const errorDetailsStyles: CSSProperties = {
  fontFamily: 'monospace',
  fontSize: '0.75rem',
  color: 'var(--color-ink-subtle)',
  backgroundColor: 'var(--color-surface)',
  padding: '12px 16px',
  borderRadius: 'var(--radius-md)',
  maxWidth: '100%',
  overflow: 'auto',
  marginBottom: '24px',
  textAlign: 'left',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}

const actionsStyles: CSSProperties = {
  display: 'flex',
  gap: '12px',
}

// ============================================
// Icons
// ============================================

function ErrorIcon(): ReactNode {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
      <path
        d="M16 10v8M16 22h.01"
        stroke="var(--color-error)"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="16" cy="16" r="12" stroke="var(--color-error)" strokeWidth="2" />
    </svg>
  )
}

// ============================================
// Component
// ============================================

/**
 * Error boundary component that catches JavaScript errors in child components.
 * Displays a friendly error message with retry functionality.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error)
    console.error('Component stack:', errorInfo.componentStack)
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div style={containerStyles} role="alert">
          <div style={iconContainerStyles}>
            <ErrorIcon />
          </div>
          <h1 style={titleStyles}>Something went wrong</h1>
          <p style={messageStyles}>
            An unexpected error occurred. Please try again, or reload the page if the problem
            persists.
          </p>
          {this.state.error && import.meta.env.DEV && (
            <div style={errorDetailsStyles}>{this.state.error.message}</div>
          )}
          <div style={actionsStyles}>
            <Button variant="secondary" size="md" onClick={this.handleRetry}>
              Try Again
            </Button>
            <Button variant="primary" size="md" onClick={this.handleReload}>
              Reload Page
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
